import * as XLSX from "xlsx";

/**
 * Export Monthly Attendance + OT data as a payroll-ready Excel file.
 */
export function exportPayrollExcel({ report, kitchenName, otRatePerHour = 0 }) {
  const wb = XLSX.utils.book_new();
  const { year, month, staff = [] } = report;
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });

  // ── Build sheet data ─────────────────────────────────────────────────────
  const rows = [];

  // Title block
  rows.push(["MANNA TRUST OPERATIONS"]);
  rows.push(["Monthly Attendance & Overtime Report"]);
  rows.push([`Period: ${monthName}`, "", `Kitchen: ${kitchenName}`]);
  rows.push([`Generated: ${new Date().toLocaleDateString("en-IN")}`]);
  rows.push([]); // blank

  // Header row
  const headers = [
    "S.No.", "Employee ID", "Name",
    "Days Present", "Total Hours",
    "OT Hours",
    ...(otRatePerHour > 0 ? [`OT Rate (₹/hr)`, "OT Amount (₹)"] : []),
    "Late Days", "OT Approved By", "OT Reason"
  ];
  rows.push(headers);

  // Data rows
  let totalDays = 0, totalHours = 0, totalOT = 0, totalOTAmount = 0, totalLate = 0;
  staff.forEach((s, i) => {
    const otAmount = otRatePerHour > 0 ? Math.round(s.ot_hours * otRatePerHour * 100) / 100 : null;
    const latestApproval = s.records?.[0];
    const row = [
      i + 1, s.employee_id, s.staff_name,
      s.days_present, s.total_hours,
      s.ot_hours,
      ...(otRatePerHour > 0 ? [otRatePerHour, otAmount] : []),
      s.late_days,
      latestApproval?.ot_approved_by || "",
      latestApproval?.ot_reason || ""
    ];
    rows.push(row);
    totalDays   += s.days_present;
    totalHours  += s.total_hours;
    totalOT     += s.ot_hours;
    totalLate   += s.late_days;
    if (otRatePerHour > 0) totalOTAmount += otAmount || 0;
  });

  // Totals row
  const totalsRow = [
    "", "TOTALS", "",
    totalDays, Math.round(totalHours * 100) / 100,
    Math.round(totalOT * 100) / 100,
    ...(otRatePerHour > 0 ? ["", Math.round(totalOTAmount * 100) / 100] : []),
    totalLate, "", ""
  ];
  rows.push([]);
  rows.push(totalsRow);
  rows.push([]);
  rows.push([`Total Staff: ${staff.length}`, "", `Total OT: ${Math.round(totalOT * 100) / 100}h`, ...(otRatePerHour > 0 ? [`Total OT Pay: ₹${Math.round(totalOTAmount * 100) / 100}`] : [])]);

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // ── Styling (column widths) ───────────────────────────────────────────────
  ws["!cols"] = [
    { wch: 6 }, { wch: 14 }, { wch: 22 },
    { wch: 14 }, { wch: 14 }, { wch: 10 },
    ...(otRatePerHour > 0 ? [{ wch: 14 }, { wch: 14 }] : []),
    { wch: 10 }, { wch: 18 }, { wch: 30 }
  ];

  // Merge title cells
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Payroll Report");

  // ── Attendance Details Sheet ──────────────────────────────────────────────
  if (staff.some(s => s.records?.length > 0)) {
    const detailRows = [
      ["DAILY ATTENDANCE DETAILS", "", monthName],
      [],
      ["Employee", "Date", "Check In", "Check Out", "Hours", "OT Hours", "Late?", "OT Approved By"]
    ];
    staff.forEach(s => {
      (s.records || []).forEach(r => {
        detailRows.push([
          s.staff_name, r.date,
          r.check_in ? new Date(r.check_in).toLocaleTimeString("en-IN") : "",
          r.check_out ? new Date(r.check_out).toLocaleTimeString("en-IN") : "",
          r.hours_worked || "", r.ot_hours || "",
          r.is_late ? "Yes" : "No",
          r.ot_approved_by || ""
        ]);
      });
    });
    const ws2 = XLSX.utils.aoa_to_sheet(detailRows);
    ws2["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Daily Details");
  }

  // ── Download ──────────────────────────────────────────────────────────────
  const fileName = `Payroll_${kitchenName.replace(/\s+/g, "_")}_${monthName.replace(/\s+/g, "_")}.xlsx`;
  XLSX.writeFile(wb, fileName);
}


/**
 * Export payroll as printable HTML (opens print dialog for PDF)
 */
export function printPayrollPDF({ report, kitchenName, otRatePerHour = 0 }) {
  const { year, month, staff = [] } = report;
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  let totalHours = 0, totalOT = 0, totalOTAmt = 0, totalLate = 0;
  staff.forEach(s => { totalHours += s.total_hours; totalOT += s.ot_hours; totalLate += s.late_days; if (otRatePerHour > 0) totalOTAmt += s.ot_hours * otRatePerHour; });

  const html = `<!DOCTYPE html><html><head><title>Payroll ${monthName}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; color: #3A322C; }
    h1 { color: #E8956D; margin-bottom:4px; font-size: 20px; }
    h2 { font-size:14px; color:#5C5147; margin:0 0 4px 0; font-weight:normal; }
    .meta { font-size:12px; color:#8A7E73; margin-bottom:16px; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th { background:#E8956D; color:white; padding:8px 6px; text-align:left; font-size:11px; }
    td { padding:7px 6px; border-bottom:1px solid #E5E1DB; }
    tr:nth-child(even) td { background:#FAFAF8; }
    .totals td { background:#F4F3F0 !important; font-weight:bold; border-top:2px solid #E8956D; }
    .footer { margin-top:20px; font-size:11px; color:#8A7E73; }
    @media print { button { display:none; } }
  </style></head><body>
  <h1>MANNA TRUST OPERATIONS</h1>
  <h2>Monthly Attendance & Overtime Report</h2>
  <p class="meta">Period: ${monthName} &nbsp;|&nbsp; Kitchen: ${kitchenName} &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString("en-IN")}</p>
  <table>
    <thead><tr>
      <th>S.No.</th><th>Emp ID</th><th>Name</th>
      <th>Days Present</th><th>Total Hours</th><th>OT Hours</th>
      ${otRatePerHour > 0 ? `<th>OT Amount (₹)</th>` : ""}
      <th>Late Days</th>
    </tr></thead>
    <tbody>
      ${staff.map((s, i) => `<tr>
        <td>${i + 1}</td><td>${s.employee_id}</td><td>${s.staff_name}</td>
        <td>${s.days_present}</td><td>${s.total_hours}h</td>
        <td>${s.ot_hours}h</td>
        ${otRatePerHour > 0 ? `<td>₹${Math.round(s.ot_hours * otRatePerHour * 100) / 100}</td>` : ""}
        <td>${s.late_days}</td>
      </tr>`).join("")}
    </tbody>
    <tfoot><tr class="totals">
      <td colspan="3">TOTALS (${staff.length} employees)</td>
      <td>—</td><td>${Math.round(totalHours * 100) / 100}h</td>
      <td>${Math.round(totalOT * 100) / 100}h</td>
      ${otRatePerHour > 0 ? `<td>₹${Math.round(totalOTAmt * 100) / 100}</td>` : ""}
      <td>${totalLate}</td>
    </tr></tfoot>
  </table>
  <div class="footer">Manna Trust Operations Platform &nbsp;&nbsp; This is a system-generated report.</div>
  <br><button onclick="window.print()">Print / Save as PDF</button>
  </body></html>`;

  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 600);
}
